import React, { useMemo, useState } from 'react';
import styles from './SpeechToText.module.scss';
import Icon from './Icon';
import { customColors, getPrevSegmentAndId } from '../utilities';
import { Text } from './typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../utilities/translator/translation_keys';
import classNames from 'classnames';
import { useBlocker, usePreventBrowserUnload } from '../hooks/misc';
import { useModal } from '../providers/ModalProvider';
import { useVoiceRecorder } from '../providers/VoiceRecorderProvider';
import { useOnlineStatus } from '../hooks/offline-hooks/offline-misc';
import { useLocation, useNavigate } from 'react-router-dom';
import TextWithIcon from './typography/TextWithIcon';
import { usePersistentUnclear } from '../utilities/inspection';
import Spacer from './typography/Spacer';

export default function VoiceRecorder({
  className,
  onNavigation = () => {},
  blockNavigation = false,
  onStop = async () => {},
  onStart = async () => {},
  hasRecording = false,
}) {
  const { t } = useTranslation();
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const { openConfirmationModal } = useModal();
  const online = useOnlineStatus();
  const navigate = useNavigate();

  const {
    isRecording,
    startRecording,
    recordingUrl,
    isSpeaking,
    isClearVoice,
    voiceLevel,
    activityLevel,
  } = useVoiceRecorder();

  const unclearPersistent = usePersistentUnclear(isSpeaking, isClearVoice, {
    holdMs: 1500,
    releaseMs: 800,
  });

  const location = useLocation();

  const [recordingPrevSegment, recordingInspectionId] = useMemo(
    () =>
      isRecording ? getPrevSegmentAndId(recordingUrl?.current) : [null, null],
    [recordingUrl, isRecording],
  );

  const [pathPrevSegment, currentInspectionId] = useMemo(
    () => getPrevSegmentAndId(location.pathname),
    [location.pathname],
  );

  const isOnRecordingPage = useMemo(() => {
    return (
      recordingPrevSegment === 'assessment' &&
      pathPrevSegment === 'assessment' &&
      !!recordingInspectionId &&
      !!currentInspectionId &&
      recordingInspectionId === currentInspectionId &&
      online &&
      isRecording
    );
  }, [
    isRecording,
    online,
    recordingPrevSegment,
    recordingInspectionId,
    pathPrevSegment,
    currentInspectionId,
  ]);

  usePreventBrowserUnload(blockNavigation && isRecording);

  useBlocker((tx) => {
    if (isRecording && !!blockNavigation) {
      openConfirmationModal({
        variant: 'danger',
        title: 'Recording In Progress',
        description:
          'Leaving this page will stop your current recording. Are you sure you want to Exit? You may lose your audio file.',
        onSubmit: () => {
          onNavigation();
          if (pendingNavigation) pendingNavigation();
        },
      });
      setPendingNavigation(() => () => tx.retry());
    } else {
      tx.retry();
    }
  }, isRecording);

  if (
    !isOnRecordingPage &&
    recordingPrevSegment === 'assessment' &&
    !!recordingInspectionId
  ) {
    return (
      <div className={styles.container}>
        <Icon style={{ fontSize: '2.5rem', opacity: 0.5 }}>mic_off</Icon>
        <Text color="secondary" size="sm">
          Recording in progress{' '}
          <span
            className={styles.link}
            onClick={() => {
              navigate(recordingUrl.current);
            }}
          >
            here
          </span>
        </Text>
      </div>
    );
  }

  return !online ? (
    <div className={styles.container}>
      <Icon style={{ fontSize: '2.5rem', opacity: 0.5 }}>mic_off</Icon>
      <Text color="secondary" size="sm">
        Voice Recorder not available offline
      </Text>
    </div>
  ) : (
    <div className={classNames([styles.container, className])}>
      <Icon
        baseClassName="material-icons-outlined"
        style={{
          color: isRecording ? customColors.RED_LIGHT : customColors.BLUE,
          fontSize: '3rem',
        }}
        onClick={async () => {
          if (isRecording) {
            await onStop();
          } else {
            startRecording();
            onStart();
          }
        }}
        className={styles.hover}
      >
        {isRecording ? 'stop_circle' : 'mic'}
      </Icon>
      {isRecording && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 140,
              height: 8,
              background: '#eee',
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${(isSpeaking ? voiceLevel : activityLevel) * 100}%`,
                height: '100%',
                background: customColors.BLUE,
                transition: 'width 80ms linear, background-color 120ms ease',
              }}
            />
          </div>
        </div>
      )}
      {isRecording && unclearPersistent && (
        <TextWithIcon leftIcon="warning" color="orange" noMargin>
          {t(keys.assessments.VOICE_UNCLEAR)}
        </TextWithIcon>
      )}
      <Text textAlign="center" noMargin>
        {isRecording
          ? t(keys.assessments.STOP_MESSAGE)
          : t(keys.assessments.MESSAGE)}
      </Text>
      {hasRecording && (
        <TextWithIcon leftIcon="error_outlined" size="sm" weight="semiBold">
          Pressing record will append to your current audio file
        </TextWithIcon>
      )}
      <Spacer />
    </div>
  );
}
